import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import { History, Layers } from 'lucide-react'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import AppShell from './components/shell/AppShell'
import { NichtGefunden, NurAdmin, NurAngemeldet } from './components/shell/Zugang'

// UI-Neubau (12.09.2026)
import AktiePage from './pages/AktiePage'
import GlossarPage from './pages/GlossarPage'
import AnfragenPage from './pages/AnfragenPage'
import ImportsPage from './pages/ImportsPage'
import ProFreigabePage from './pages/ProFreigabePage'
import KontoPage from './pages/KontoPage'
import PasswortNeuPage from './pages/PasswortNeuPage'
import PasswortVergessenPage from './pages/PasswortVergessenPage'
import LensScreenerPage from './pages/LensScreenerPage'
import PlaceholderPage from './pages/PlaceholderPage'
import StartPage from './pages/StartPage'
import SystemStatusPage from './pages/SystemStatusPage'
import TopIdeasPage from './pages/TopIdeasPage'
import UnterbewertetPage from './pages/UnterbewertetPage'

// Bestehende Seiten -- laufen in der neuen Huelle weiter.
import AgbPage from './pages/AgbPage'
import DatenschutzPage from './pages/DatenschutzPage'
import ImpressumPage from './pages/ImpressumPage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ScreenerPage from './pages/ScreenerPage'
import WatchlistPage from './pages/WatchlistPage'

/** Alte Adresse der Detailseite (bis 15.09.2026, nur Admin) -- leitet weiter. */
function AlteDetailseite() {
  const { symbol = '' } = useParams<{ symbol: string }>()
  return <Navigate to={`/aktie/${encodeURIComponent(symbol)}`} replace />
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<AppShell />}>
            {/* Fuer alle. Start zeigt Besuchern einen Ausschnitt der Ideen (Kim, 13.09.2026). */}
            <Route index element={<StartPage />} />
            <Route path="glossar" element={<GlossarPage />} />
            {/* Einzelansicht je Aktie (15.09.2026): fuer alle erreichbar, das Backend stuft ab. */}
            <Route path="aktie/:symbol" element={<AktiePage />} />

            {/* Nach der Anmeldung. Screener und Top-Ideen sperren selbst. */}
            <Route path="screener" element={<LensScreenerPage />} />
            <Route path="ideen" element={<TopIdeasPage />} />
            <Route path="unterbewertet" element={<UnterbewertetPage />} />
            <Route
              path="watchlist"
              element={
                <NurAngemeldet titel="Die Watchlist ist für angemeldete Nutzer.">
                  <WatchlistPage />
                </NurAngemeldet>
              }
            />

            {/* Nur Admin -- fuer alle anderen gibt es diese Seiten nicht. */}
            <Route
              path="rueckvergleich"
              element={
                <NurAdmin>
                  <PlaceholderPage
                    icon={History}
                    text="Der Rückvergleich prüft, wie sich die Urteile der Linsen an vergangenen Stichtagen bewährt haben, jeweils gegen die Grunddrift desselben Zeitraums. Der erste Lauf über 31 Stichtage von 2019 bis 2026 ist gerechnet; die Darstellung folgt hier."
                    punkte={[
                      'Trefferquote, Median, Mittelwert und Vervielfacher-Quote je Linse und Urteil',
                      'Getrennt nach ganzem Universum und anlegbarer Teilmenge ab 300 Mio. USD',
                      'Rückwärts gerechnete Stichtage getrennt von künftig fortgeschriebenen',
                    ]}
                  />
                </NurAdmin>
              }
            />
            <Route
              path="groessen-split"
              element={
                <NurAdmin>
                  <PlaceholderPage
                    icon={Layers}
                    text="Der Größen-Split zeigt, in welcher Größenklasse eine Linse wirkt. Jede Klasse wird gegen ihre eigene Drift gemessen, damit eine Linse mit vielen kleinen Titeln nicht besser aussieht, als sie ist."
                    punkte={[
                      'Micro unter 300 Mio., Small bis 2 Mrd., Mid bis 10 Mrd., Large darüber',
                      'Vorsprung je Linse, Urteil und Horizont innerhalb der Klasse',
                      'Explorativ: zeigt Hypothesen, belegt nichts',
                    ]}
                  />
                </NurAdmin>
              }
            />
            <Route path="importe" element={<NurAdmin><ImportsPage /></NurAdmin>} />
            <Route path="anfragen" element={<NurAdmin><AnfragenPage /></NurAdmin>} />
            {/* Ohne Anmeldung: Der Link aus der E-Mail traegt das Token (23.09.2026). */}
            <Route path="pro-freigabe/:token" element={<ProFreigabePage />} />
            {/* Passwort: anfordern und setzen ohne Anmeldung, aendern mit (04.10.2026). */}
            <Route path="passwort-vergessen" element={<PasswortVergessenPage />} />
            <Route path="passwort-neu/:token" element={<PasswortNeuPage />} />
            <Route path="konto" element={
              <NurAngemeldet titel="Dein Konto siehst du nach der Anmeldung.">
                <KontoPage />
              </NurAngemeldet>
            } />
            <Route path="status" element={<NurAdmin><SystemStatusPage /></NurAdmin>} />

            {/* Aus der Zeit vor dem Neubau, nur fuer den Admin: der alte Kennzahlen-Filter
                (vielleicht spaeter eine Pro-Funktion). /markt, /dashboard, /about und /admin
                sind am 13.09.2026 entfallen (Kim), /stock/... leitet seit 15.09. weiter. */}
            <Route path="screener/kennzahlen" element={<NurAdmin><ScreenerPage /></NurAdmin>} />
            <Route path="stock/:symbol" element={<AlteDetailseite />} />

            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />
            <Route path="impressum" element={<ImpressumPage />} />
            <Route path="datenschutz" element={<DatenschutzPage />} />
            <Route path="agb" element={<AgbPage />} />
            <Route path="*" element={<NichtGefunden />} />
          </Route>
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
